/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';

import { useAppDispatch, useAppSelector } from '../../../../../../../app/hooks';
import { clearMedicationState, fetchMedicationData, selectMedicationState } from './medicationSlice';
import { selectCreatorMode, selectQueryMeds } from '../../../../chatSlice';
import { selectUserFlags } from '../../../../../Login/userSlice';
import InfoIcon from './InfoIcon';
import MedicationSuggestionIcon from './medicationSuggestionIcon';
import styles from './Medication.module.scss';
import { OPEN_FDA_URL } from '../../utils';

// Functional component in TypeScript
const Medication = () => {
  // State to keep track of the currently open accordion (-1 means none is open)
  const [expanded, setExpanded] = useState<number>(-1);
  const medicationRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useAppDispatch();

  const {
    result: { name: medicationName, main_sections, extended_sections },
    suggestions,
    loadingMedicationData,
  } = useAppSelector(selectMedicationState);

  const queryMeds = useAppSelector(selectQueryMeds);
  const flags = useAppSelector(selectUserFlags);
  const isCreator = useAppSelector(selectCreatorMode);

  // Function to handle scrolling to the target element
  const scrollToElement = () => {
    if (medicationRef.current) {
      medicationRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (flags?.consumer_medication_search && !isCreator && queryMeds?.[0]) {
      void dispatch(
        fetchMedicationData({
          query: `query=${queryMeds[0]}&index=0`,
        })
      );
    }
    return () => void dispatch(clearMedicationState());
  }, [dispatch, queryMeds, flags?.consumer_medication_search, isCreator]);

  // Sections of the accordion
  const [sections, setSections] = useState(main_sections);

  const hasHiddenSections = sections.length !== main_sections.length + extended_sections.length;

  const handleShowMoreOrLess = () => {
    hasHiddenSections ? setSections([...sections, ...extended_sections]) : setSections(main_sections);
  };

  const openSuggestedLink = (href: string) => {
    setExpanded(-1);
    scrollToElement();
    void dispatch(
      fetchMedicationData({
        query: href,
      })
    );
  };

  useEffect(() => {
    setSections(main_sections);
  }, [main_sections]);

  // Handle accordion state change
  const handleChange = (index: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? index : -1);
  };

  // render nothing if no queryMeds present
  if (!medicationName || !flags?.consumer_medication_search || isCreator) return null;

  return (
    <div className={styles.medicationContainer} ref={medicationRef}>
      {loadingMedicationData ? (
        <CircularProgress disableShrink className={styles.loader} />
      ) : (
        <>
          <Typography gutterBottom className={styles.heading}>
            {medicationName}
          </Typography>
          {sections.map((section, index) => (
            <Accordion
              key={index}
              expanded={expanded === index}
              onChange={handleChange(index)}
              className={styles.accordion}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel${index}-content`}
                id={`panel${index}-header`}
                className={styles.accordionSummary}
              >
                <Typography className={styles.accordionLabel}>{section.label || ''}</Typography>
              </AccordionSummary>
              <AccordionDetails className={styles.accordionDetails}>
                <Typography>
                  <div className={styles.content} dangerouslySetInnerHTML={{ __html: section.content || '' }} />
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
          <Link
            component="button"
            variant="body2"
            align="center"
            className={styles.linkButton}
            onClick={handleShowMoreOrLess}
          >
            Show {hasHiddenSections ? 'more' : 'less'} {hasHiddenSections ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </Link>
          <Box className={styles.suggestionContainer}>
            <div style={{ marginLeft: '4px' }}>
              <Typography className={styles.suggestionHeading}>Not what you expected? Try these</Typography>
              {suggestions?.map((list, index) => (
                <Typography
                  id={`suggestion${index}`}
                  onClick={() => openSuggestedLink(list._href)}
                  className={styles.suggestions}
                  variant="subtitle2"
                  component={Link}
                >
                  <MedicationSuggestionIcon />
                  <span className={styles.suggestionName}>{list?.name}</span>
                </Typography>
              ))}
            </div>
          </Box>
          <div className={styles.disclaimer}>
            <InfoIcon />
            <Typography className={styles.disclaimerText}>
              Content is sourced from OpenFDA via an API, covering on-label uses only. It does not replace professional
              medical advice or address off-label uses. See more information on their standards
              <a href={OPEN_FDA_URL} target="_blank" rel="noopener noreferrer" className={styles.openFDA}>
                here.
              </a>
            </Typography>
          </div>
        </>
      )}
    </div>
  );
};

export default Medication;
